// @ts-nocheck
/**
 * Custom Quick Add Component for Mobile Variant Selection
 * Handles mobile variant modal interactions and form submissions
 */

class CustomQuickAddComponent extends HTMLElement {
  constructor() {
    super();
    this.productId = this.dataset.productId;
    this.productTitle = this.dataset.productTitle;
    /** @type {Record<number, string>} */
    this.selectedOptions = {};
    this.currentVariant = null;
    this.productData = null;
    this._modalReturnPoint = null;
    
    this.init();
  }

  init() {
    // Get product data from JSON script tag
    const productJsonScript = this.querySelector(`script[data-product-json="${this.productId}"]`);
    if (productJsonScript && productJsonScript.textContent) {
      this.productData = JSON.parse(productJsonScript.textContent);
    }

    // Initialize mobile variant modal
    this.initMobileModal();
    
    // Initialize desktop variant selection
    this.initDesktopVariants();
  }

  initMobileModal() {
    const mobileTrigger = this.querySelector('.mobile-variant-trigger');
    const mobileModal = this.querySelector('.mobile-variant-modal');
    const closeButton = mobileModal?.querySelector('.mobile-variant-close');
    const overlay = mobileModal?.querySelector('.mobile-variant-overlay');

    if (mobileTrigger && mobileModal) {
      // Open modal on trigger click
      mobileTrigger.addEventListener('click', () => {
        this.openMobileModal(mobileModal);
      });

      // Close modal on close button click
      if (closeButton) {
        closeButton.addEventListener('click', () => {
          this.closeMobileModal(mobileModal);
        });
      }

      // Close modal on overlay click
      if (overlay) {
        overlay.addEventListener('click', () => {
          this.closeMobileModal(mobileModal);
        });
      }

      // Close modal on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && mobileModal.classList.contains('is-open')) {
          this.closeMobileModal(mobileModal);
        }
      });

      // Initialize mobile option selection
      this.initMobileOptions(mobileModal);
    }
  }

  initDesktopVariants() {
    const desktopOptions = this.querySelectorAll('.desktop-variant-selector .custom-option-value');
    
    desktopOptions.forEach((el) => {
      const option = /** @type {HTMLButtonElement} */ (el);
      option.addEventListener('click', () => {
        if (option.disabled) return;
        
        const optionPosition = Number(option.dataset.optionPosition);
        const value = option.dataset.value;
        
        // Update selected options
        this.selectedOptions[optionPosition] = value;
        
        // Update UI
        this.updateDesktopOptionSelection(option);
        
        // Find and update variant
        this.updateVariant();
      });
    });
  }

  initMobileOptions(mobileModal) {
    const mobileOptions = mobileModal.querySelectorAll('.mobile-option-value');
    
    mobileOptions.forEach((el) => {
      const option = /** @type {HTMLButtonElement} */ (el);
      option.addEventListener('click', () => {
        if (option.disabled) return;
        
        const optionPosition = Number(option.dataset.optionPosition);
        const value = option.dataset.value;
        
        // Update selected options
        this.selectedOptions[optionPosition] = value;
        
        // Update UI
        this.updateMobileOptionSelection(option);
        
        // Find and update variant (auto-add will be triggered here)
        this.updateVariant();
      });
    });
  }

  openMobileModal(modal) {
    // Portal modal to body to avoid being constrained by transformed ancestors
    if (!this._modalReturnPoint) {
      this._modalReturnPoint = {
        parent: modal.parentElement,
        nextSibling: modal.nextSibling,
      };
    }

    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }

    modal.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    
    // Focus on first available option
    const firstOption = modal.querySelector('.mobile-option-value:not(.disabled)');
    if (firstOption) {
      /** @type {HTMLElement} */ (firstOption).focus();
    }
  }

  closeMobileModal(modal) {
    modal.classList.remove('is-open');
    document.body.style.overflow = '';

    // Restore modal back to original location
    if (this._modalReturnPoint?.parent) {
      const { parent, nextSibling } = this._modalReturnPoint;
      if (nextSibling && nextSibling.parentNode === parent) {
        parent.insertBefore(modal, nextSibling);
      } else {
        parent.appendChild(modal);
      }
    }
  }

  updateDesktopOptionSelection(clickedOption) {
    const optionPosition = clickedOption.dataset.optionPosition;
    const desktopOptions = this.querySelectorAll(`.desktop-variant-selector .custom-option-value[data-option-position="${optionPosition}"]`);
    
    desktopOptions.forEach((el) => {
      /** @type {HTMLElement} */ (el).classList.remove('selected');
    });
    
    clickedOption.classList.add('selected');
  }

  updateMobileOptionSelection(clickedOption) {
    const optionPosition = clickedOption.dataset.optionPosition;
    const mobileOptions = this.querySelectorAll(`.mobile-variant-modal .mobile-option-value[data-option-position="${optionPosition}"]`);
    
    mobileOptions.forEach((el) => {
      /** @type {HTMLElement} */ (el).classList.remove('selected');
    });
    
    clickedOption.classList.add('selected');
  }

  updateVariant() {
    if (!this.productData) return;

    // Find the matching variant
    const variant = this.productData.variants.find((variant) => {
      return this.productData.options.every((option, index) => {
        const selectedValue = this.selectedOptions[option.position];
        return !selectedValue || variant.options[index] === selectedValue;
      });
    });

    if (variant) {
      this.currentVariant = variant;
      this.updateVariantUI(variant);
      this.updateFormInputs(variant);
      
      // Automatically add to cart when variant is selected
      this.autoAddToCart(variant);
    }
  }

  updateVariantUI(variant) {
    // Update mobile trigger button text based on availability
    const mobileTrigger = this.querySelector('.mobile-variant-trigger');
    const buttonText = mobileTrigger?.querySelector('.button-text');
    
    if (mobileTrigger) {
      /** @type {HTMLButtonElement} */ (mobileTrigger).disabled = !variant.available;
      mobileTrigger.classList.toggle('disabled', !variant.available);
    }

    if (buttonText) {
      buttonText.textContent = variant.available 
        ? (window.theme?.strings?.products?.product?.add_to_cart || 'Add to cart')
        : (window.theme?.strings?.products?.product?.sold_out || 'Sold out');
    }

    // Update variant ID in form
    const variantInputs = this.querySelectorAll('input[name="id"]');
    variantInputs.forEach((input) => {
      /** @type {HTMLInputElement} */ (input).value = String(variant.id);
    });
  }

  updateFormInputs(variant) {
    // Update hidden variant ID inputs
    const variantInputs = this.querySelectorAll('input[name="id"]');
    variantInputs.forEach((input) => {
      /** @type {HTMLInputElement} */ (input).value = String(variant.id);
    });
  }

  async autoAddToCart(variant) {
    // Don't auto-add if variant is not available
    if (!variant.available) return;

    // Show loading state on mobile trigger button
    const mobileTrigger = this.querySelector('.mobile-variant-trigger');
    const loadingSpinner = mobileTrigger?.querySelector('.loading__spinner');
    const buttonText = mobileTrigger?.querySelector('.button-text');

    if (mobileTrigger) {
      /** @type {HTMLButtonElement} */ (mobileTrigger).disabled = true;
      if (loadingSpinner) loadingSpinner.classList.remove('visually-hidden');
      if (buttonText) buttonText.style.opacity = '0.5';
    }

    try {
      const formData = new FormData();
      formData.set('id', String(variant.id));
      formData.set('quantity', '1');

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        
        // Trigger cart update event
        this.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { items: [result] },
          bubbles: true
        }));

        // Show success message
        this.showSuccessMessage();
        
        // Close mobile modal if open
        const mobileModal = this.querySelector('.mobile-variant-modal');
        if (mobileModal?.classList.contains('is-open')) {
          this.closeMobileModal(mobileModal);
        }
      } else {
        throw new Error('Failed to add to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showErrorMessage();
    } finally {
      // Reset loading state
      if (mobileTrigger) {
        /** @type {HTMLButtonElement} */ (mobileTrigger).disabled = false;
        if (loadingSpinner) loadingSpinner.classList.add('visually-hidden');
        if (buttonText) buttonText.style.opacity = '1';
      }
    }
  }

  showSuccessMessage() {
    // Create success message
    const message = document.createElement('div');
    message.className = 'quick-add-success-message';
    message.textContent = window.theme?.strings?.products?.product?.added_to_cart || 'Added to cart';
    message.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #4CAF50;
      color: white;
      padding: 12px 20px;
      border-radius: 4px;
      z-index: 10000;
      animation: slideIn 0.3s ease;
    `;

    document.body.appendChild(message);

    // Remove after 3 seconds
    setTimeout(() => {
      message.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => {
        if (message.parentNode) {
          message.parentNode.removeChild(message);
        }
      }, 300);
    }, 3000);
  }

  showErrorMessage() {
    // Create error message
    const message = document.createElement('div');
    message.className = 'quick-add-error-message';
    message.textContent = window.theme?.strings?.products?.product?.error_adding_to_cart || 'Error adding to cart';
    message.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #f44336;
      color: white;
      padding: 12px 20px;
      border-radius: 4px;
      z-index: 10000;
      animation: slideIn 0.3s ease;
    `;

    document.body.appendChild(message);

    // Remove after 3 seconds
    setTimeout(() => {
      message.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => {
        if (message.parentNode) {
          message.parentNode.removeChild(message);
        }
      }, 300);
    }, 3000);
  }
}

// Add CSS animations for messages
const style = document.createElement('style');
style.textContent = `
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @keyframes slideOut {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(100%);
      opacity: 0;
    }
  }
`;
document.head.appendChild(style);

// Register the custom element
customElements.define('custom-quick-add-component', CustomQuickAddComponent); 